<template>
  <component
    :is="component"
    v-model="localValue"
    v-bind="componentProps"
  >
    <template #activator="{ props }">
      <slot name="activator" :props="props" />
    </template>

    <template #handle="slotData">
      <slot name="sheet-handle" v-bind="slotData" />
    </template>

    <v-card flat color="neutral25" class="rounded-lg">
      <slot v-if="!isDesktop" name="sheet-prepend">
        <h4
          v-if="sheetTitle"
          class="text-text text-h5 font-weight-semibold px-4 py-3"
          v-text="sheetTitle"
        />
      </slot>
      <slot />
    </v-card>

  </component>
</template>

<script setup>
import { computed, defineProps, defineEmits, defineExpose, defineOptions } from 'vue'
import { VMenu } from 'vuetify/components'
import { useDisplay } from 'vuetify'
import BottomSheetDraggable from '@/components/global/BottomSheetDraggable.vue'
import { lockPositionScrollStrategy } from '@/utils/scroll-strategies'

// Set inheritAttrs to false
defineOptions({
  inheritAttrs: false,
})

// Define props
const props = defineProps({
    modelValue: Boolean,
    sheetTitle: {
      type: String,
      default: null,
    },
    menuProps: {
      type: Object,
      default: null,
    },
    sheetProps: {
      type: Object,
      default: null,
    },
    disabled: Boolean,
})

// Define emits
const emit = defineEmits(['update:modelValue'])

// Use display composable for breakpoints
const { mdAndUp } = useDisplay()

// Create a computed v-model with getter/setter
const localValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  }
})

const isDesktop = computed(() => mdAndUp.value)

const component = computed(() => {
  if (isDesktop.value) return VMenu
  return BottomSheetDraggable
})

const componentProps = computed(() => {
  if (isDesktop.value) {
        const defaultMenuProps = {
          locationStrategy: 'connected',
          scrollStrategy: lockPositionScrollStrategy,
          location: 'bottom',
          origin: 'top',
          offset: [12, 0],
          rounded: 'lg',
          disabled: props.disabled,
          contained: false,
        }

        // Merge with menuProps, allowing menuProps to override defaults
        return {
          ...defaultMenuProps,
          ...props.menuProps,
        }
      }

      return {
        inset: true,
        disabled: props.disabled,
    ...props.sheetProps,
      }
})

// Methods
const close = () => {
  localValue.value = false
}

// Expose close method
defineExpose({ close })
</script>
